import type { DraftPersistenceScope } from './named-project-repository';

const MAX_PREVIEW_BYTES = 160 * 1024;
const MAX_PREVIEW_COUNT = 32;
const MAX_TOTAL_BYTES = 5 * 1024 * 1024;

interface PreviewRecord {
  projectId: string;
  savedAt: string;
  blob: Blob;
  byteLength: number;
}

function databaseName(scope?: DraftPersistenceScope): string {
  return `buncha-coasters-draft-previews:${scope?.key ?? 'default'}`;
}

function openDatabase(name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, 1);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains('previews'))
        database.createObjectStore('previews', { keyPath: 'projectId' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error('Draft preview storage is unavailable'));
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Draft preview request failed'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('Draft preview write failed'));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error('Draft preview write aborted'));
  });
}

export class DraftPreviewRepository {
  private database: IDBDatabase | null = null;
  private readonly memory = new Map<string, PreviewRecord>();

  constructor(private readonly scope?: DraftPersistenceScope) {}

  async load(projectId: string): Promise<Blob | null> {
    try {
      const database = await this.getDatabase();
      const transaction = database.transaction('previews', 'readonly');
      const value = await requestResult(
        transaction.objectStore('previews').get(projectId) as IDBRequest<PreviewRecord | undefined>,
      );
      return value?.blob ?? null;
    } catch {
      return this.memory.get(projectId)?.blob ?? null;
    }
  }

  async save(projectId: string, blob: Blob): Promise<boolean> {
    if (blob.size > MAX_PREVIEW_BYTES) return false;
    const record: PreviewRecord = {
      projectId,
      savedAt: new Date().toISOString(),
      blob,
      byteLength: blob.size,
    };
    try {
      const database = await this.getDatabase();
      const transaction = database.transaction('previews', 'readwrite');
      transaction.objectStore('previews').put(record);
      await transactionDone(transaction);
      await this.enforceLimits(database);
      return true;
    } catch {
      this.memory.set(projectId, record);
      this.enforceMemoryLimits();
      return true;
    }
  }

  async delete(projectId: string): Promise<void> {
    this.memory.delete(projectId);
    try {
      const database = await this.getDatabase();
      const transaction = database.transaction('previews', 'readwrite');
      transaction.objectStore('previews').delete(projectId);
      await transactionDone(transaction);
    } catch {
      // Preview cleanup is fail-soft and must never affect the saved draft.
    }
  }

  dispose(): void {
    this.database?.close();
    this.database = null;
    this.memory.clear();
  }

  private async getDatabase(): Promise<IDBDatabase> {
    if (this.database) return this.database;
    this.database = await openDatabase(databaseName(this.scope));
    return this.database;
  }

  private async enforceLimits(database: IDBDatabase): Promise<void> {
    const transaction = database.transaction('previews', 'readwrite');
    const store = transaction.objectStore('previews');
    const records = await requestResult(store.getAll() as IDBRequest<PreviewRecord[]>);
    records.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
    let total = 0;
    records.forEach((record, index) => {
      total += record.byteLength;
      if (index >= MAX_PREVIEW_COUNT || total > MAX_TOTAL_BYTES) store.delete(record.projectId);
    });
    await transactionDone(transaction);
  }

  private enforceMemoryLimits(): void {
    const records = [...this.memory.values()].sort((a, b) => b.savedAt.localeCompare(a.savedAt));
    let total = 0;
    records.forEach((record, index) => {
      total += record.byteLength;
      if (index >= MAX_PREVIEW_COUNT || total > MAX_TOTAL_BYTES)
        this.memory.delete(record.projectId);
    });
  }
}

export async function createDraftPreview(source: Blob): Promise<Blob | null> {
  try {
    const image = await createImageBitmap(source);
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 360;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    image.close();
    const qualities = [0.76, 0.64, 0.52] as const;
    for (const quality of qualities) {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/webp', quality),
      );
      if (blob && blob.size <= MAX_PREVIEW_BYTES) return blob;
    }
    return null;
  } catch {
    return null;
  }
}
